import { Input, Select, Row, Col, Typography, Card, Spin, Tabs, Checkbox, Popconfirm, Button, Collapse, List, message } from "antd";
import { PlusOutlined, SearchOutlined, DeleteOutlined } from "@ant-design/icons";
import QuestionCard from "./QuestionCard";
import PrimaryButton from "../../components/CustomButton/PrimaryButton"
import { useEffect, useState, useMemo } from "react";
import CustomModal from "../../components/CustomModal/CustomModal";
import AddQuestionForm from "./AddQuestionForm";
import { MODAL_WIDTHS } from "../../constants/constants";
import { useSelector, useDispatch } from "react-redux";
import { getQuestionDropdowns, getQuestions, getPredefinedThreads, createPredefinedThread, deletePredefinedThread, deleteQuestionFromPredefinedThread } from "../../redux/slices/QuestionBankSlice";
import "./QuestionBank.scss";

const { Title, Text } = Typography;
const { Option } = Select;

const QuestionBank = () => {
    const { dropdownData, questions, loading, predefinedThreads, createPredefinedThreadLoading, deletePredefinedThreadLoading, deleteQuestionFromPredefinedThreadLoading } = useSelector((state) => state.questionBank);
    const dispatch = useDispatch();
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [entityFilter, setEntityFilter] = useState("all");

    // Custom threads management states
    const [createThreadModal, setCreateThreadModal] = useState(false);
    const [newThreadName, setNewThreadName] = useState("");
    const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);

    useEffect(() => {
        dispatch(getQuestionDropdowns());
        dispatch(getPredefinedThreads());
    }, [dispatch]);

    useEffect(() => {

        const params = {};

        if (categoryFilter !== "all") {
            params.categoryId = categoryFilter;
        }

        if (entityFilter !== "all") {
            params.entityTypeId = entityFilter;
        }

        dispatch(getQuestions(params));

    }, [dispatch, categoryFilter, entityFilter]);

    const groupedQuestions = useMemo(() => {

        const filtered = questions?.filter((q) => {
            if (!searchText) return true;

            const text = searchText.toLowerCase();

            return (
                q.questionTitle?.toLowerCase().includes(text) ||
                q.description?.toLowerCase().includes(text)
            );
        });

        return filtered?.reduce((acc, question) => {
            const category = question.categoryName;

            if (!acc[category]) {
                acc[category] = [];
            }

            acc[category].push(question);

            return acc;
        }, {});

    }, [questions, searchText]);

    const handleCreateThreadSubmit = async () => {
        if (!newThreadName.trim()) {
            message.error("Please enter a custom thread name");
            return;
        }

        if (selectedQuestionIds.length === 0) {
            message.error("Please select at least one question");
            return;
        }

        const payload = {
            threadName: newThreadName.trim(),
            questionIds: selectedQuestionIds,
        };

        const res = await dispatch(createPredefinedThread(payload));
        if (res.payload?.statusCode === 201) {
            message.success("Custom thread created successfully");
            setNewThreadName("");
            setSelectedQuestionIds([]);
            setCreateThreadModal(false);
            dispatch(getPredefinedThreads());
        } else {
            message.error(res.payload || "Failed to create custom thread");
        }
    };

    const handleDeleteThread = async (id) => {
        const res = await dispatch(deletePredefinedThread(id));
        if (res.payload?.statusCode === 200) {
            message.success("Custom thread deleted successfully");
            dispatch(getPredefinedThreads());
        } else {
            message.error(res.payload || "Failed to delete custom thread");
        }
    };

    const handleDeleteQuestionFromThread = async (predefinedThreadId, questionId) => {
        const res = await dispatch(deleteQuestionFromPredefinedThread({ predefinedThreadId, questionId }));
        if (res.payload?.statusCode === 200) {
            message.success("Question removed from custom thread successfully");
            dispatch(getPredefinedThreads());
        } else {
            message.error(res.payload || "Failed to remove question from custom thread");
        }
    };

    const handleQuestionToggle = (questionId) => {
        setSelectedQuestionIds((prev) => {
            if (prev.includes(questionId)) {
                return prev.filter((id) => id !== questionId);
            } else {
                return [...prev, questionId];
            }
        });
    };

    const handleCategorySelectAll = (categoryQuestions, isChecked) => {
        const questionIds = categoryQuestions.map(q => q.questionId);
        setSelectedQuestionIds((prev) => {
            if (isChecked) {
                return Array.from(new Set([...prev, ...questionIds]));
            } else {
                return prev.filter(id => !questionIds.includes(id));
            }
        });
    };

    return (
        <div className="question-bank">
            {/* Header */}
            <div className="qb-header" style={{ marginBottom: "20px" }}>
                <div>
                    <Title level={3}>Question Bank Management</Title>
                    <Text type="secondary">
                        Create, manage, and organize onboarding questions and custom threads
                    </Text>
                </div>
            </div>

            {/* Tabs */}
            <Tabs defaultActiveKey="1">
                <Tabs.TabPane tab="Question Bank" key="1">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", marginTop: "10px" }}>
                        <div>
                            <Title level={4} style={{ margin: 0 }}>Questions Bank</Title>
                            <Text type="secondary">List of all onboarding questions</Text>
                        </div>
                        <PrimaryButton
                            icon={<PlusOutlined />}
                            onClick={() => setOpenModal(true)}
                        >
                            Add New Question
                        </PrimaryButton>
                    </div>

                    {/* Filters */}
                    <Card className="qb-filters" style={{ marginBottom: "20px" }}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12} lg={8}>
                                <Input
                                    placeholder="Search by question text or description..."
                                    prefix={<SearchOutlined />}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    allowClear
                                />
                            </Col>

                            <Col xs={24} md={12} lg={8}>
                                <Select
                                    value={categoryFilter}
                                    className="qb-cat-filter"
                                    onChange={(value) => setCategoryFilter(value)}
                                    style={{ width: "100%" }}
                                >
                                    <Option value="all">All Categories</Option>
                                    {dropdownData?.questionCatogory?.map((category) => (
                                        <Option key={category.categoryId} value={category.categoryId}>
                                            {category.categoryName}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>

                            <Col xs={24} md={12} lg={8}>
                                <Select
                                    value={entityFilter}
                                    className="qb-entity-filter"
                                    onChange={(value) => setEntityFilter(value)}
                                    style={{ width: "100%" }}
                                >
                                    <Option value="all">All Company Types</Option>
                                    {dropdownData?.entityType?.map((entityType) => (
                                        <Option key={entityType.entityTypeId} value={entityType.entityTypeId}>
                                            {entityType.entityTypeName}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                        </Row>
                    </Card>

                    <Spin spinning={loading}>
                        {Object.entries(groupedQuestions || {}).map(([category, categoryQuestions]) => (
                            <div key={category} className="qb-section">
                                <div className="qb-section-header">
                                    <Title level={5}>{category}</Title>
                                    <Text type="secondary">
                                        {categoryQuestions.length} Questions
                                    </Text>
                                </div>

                                <Row gutter={[16, 16]}>
                                    {categoryQuestions.map((q) => (
                                        <Col xs={24} md={12} lg={8} key={q.questionId}>
                                            <QuestionCard
                                                question={q}
                                                setOpenModal={setOpenModal}
                                                setSelectedQuestion={setSelectedQuestion}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        ))}
                    </Spin>
                </Tabs.TabPane>

                <Tabs.TabPane tab="Custom Threads" key="2">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", marginTop: "10px" }}>
                        <div>
                            <Title level={4} style={{ margin: 0 }}>Custom Threads</Title>
                            <Text type="secondary">Create and manage custom threads of grouped questions</Text>
                        </div>
                        <PrimaryButton
                            icon={<PlusOutlined />}
                            onClick={() => setCreateThreadModal(true)}
                        >
                            Create Custom Thread
                        </PrimaryButton>
                    </div>

                    <Spin spinning={loading}>
                        {predefinedThreads?.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>
                                No custom threads available. Click "Create Custom Thread" to add one.
                            </div>
                        ) : (
                            <Row gutter={[16, 16]}>
                                {predefinedThreads?.map((thread) => {
                                    // Get question titles for this thread
                                    const threadQuestions = questions?.filter((q) => thread.questionIds?.includes(q.questionId)) || [];

                                    return (
                                        <Col xs={24} md={12} key={thread.predefinedThreadId}>
                                            <Card
                                                title={thread.threadName}
                                                extra={
                                                    <Popconfirm
                                                        title="Delete Custom Thread"
                                                        description="Are you sure you want to delete this custom thread?"
                                                        onConfirm={() => handleDeleteThread(thread.predefinedThreadId)}
                                                        okText="Yes"
                                                        cancelText="No"
                                                    >
                                                        <Button
                                                            type="text"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            loading={deletePredefinedThreadLoading}
                                                        />
                                                    </Popconfirm>
                                                }
                                                style={{ height: "100%" }}
                                            >
                                                <Collapse expandIconPosition="end" ghost>
                                                    <Collapse.Panel header={`Questions included (${threadQuestions.length})`} key="1">
                                                        <List
                                                            dataSource={threadQuestions}
                                                            renderItem={(item, idx) => (
                                                                <List.Item
                                                                    key={item.questionId}
                                                                    actions={[
                                                                        <Popconfirm
                                                                            title="Remove Question"
                                                                            description="Are you sure you want to remove this question from this custom thread?"
                                                                            onConfirm={() => handleDeleteQuestionFromThread(thread.predefinedThreadId, item.questionId)}
                                                                            okText="Yes"
                                                                            cancelText="No"
                                                                            key="delete"
                                                                        >
                                                                            <Button
                                                                                type="text"
                                                                                danger
                                                                                icon={<DeleteOutlined />}
                                                                                loading={deleteQuestionFromPredefinedThreadLoading}
                                                                                size="small"
                                                                            />
                                                                        </Popconfirm>
                                                                    ]}
                                                                >
                                                                    <Text>{idx + 1}. {item.questionTitle}</Text>
                                                                </List.Item>
                                                            )}
                                                            size="small"
                                                        />
                                                    </Collapse.Panel>
                                                </Collapse>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        )}
                    </Spin>
                </Tabs.TabPane>
            </Tabs>

            {openModal && (
                <CustomModal
                    title={selectedQuestion ? "Edit Question" : "Add New Question"}
                    open={openModal}
                    centered
                    closeIcon
                    width={MODAL_WIDTHS}
                    onCancel={() => {
                        setOpenModal(false);
                        setSelectedQuestion(null);
                    }}
                    content={
                        <AddQuestionForm
                            onClose={() => {
                                setOpenModal(false);
                                setSelectedQuestion(null);
                            }}
                            editData={selectedQuestion}
                        />
                    }
                />
            )}

            {createThreadModal && (
                <CustomModal
                    title="Create Custom Thread"
                    open={createThreadModal}
                    centered
                    closeIcon
                    width={700}
                    onCancel={() => {
                        setCreateThreadModal(false);
                        setNewThreadName("");
                        setSelectedQuestionIds([]);
                    }}
                    footer={
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: "10px",
                                marginRight: "20px",
                                marginBottom: "15px",
                            }}
                        >
                            <PrimaryButton
                                loading={createPredefinedThreadLoading}
                                onClick={handleCreateThreadSubmit}
                            >
                                Create Thread
                            </PrimaryButton>
                        </div>
                    }
                    content={
                        <div style={{ padding: "10px 0" }}>
                            <div style={{ marginBottom: "20px" }}>
                                <Text strong>Custom Thread Name</Text>
                                <Input
                                    placeholder="e.g. Onboarding Questionnaire"
                                    value={newThreadName}
                                    onChange={(e) => setNewThreadName(e.target.value)}
                                    style={{ marginTop: "8px" }}
                                />
                            </div>

                            <Text strong style={{ display: "block", marginBottom: "10px" }}>Select Questions to include</Text>
                            <div style={{ maxHeight: "350px", overflowY: "auto", border: "1px solid #f0f0f0", borderRadius: "8px", padding: "12px" }}>
                                <Collapse defaultActiveKey={Object.keys(groupedQuestions || {})}>
                                    {Object.entries(groupedQuestions || {}).map(([category, categoryQuestions]) => {
                                        const isAllSelected = categoryQuestions.every(q => selectedQuestionIds.includes(q.questionId));
                                        const isIndeterminate = categoryQuestions.some(q => selectedQuestionIds.includes(q.questionId)) && !isAllSelected;

                                        return (
                                            <Collapse.Panel
                                                key={category}
                                                header={
                                                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }} onClick={(e) => e.stopPropagation()}>
                                                        <Checkbox
                                                            checked={isAllSelected}
                                                            indeterminate={isIndeterminate}
                                                            onChange={(e) => handleCategorySelectAll(categoryQuestions, e.target.checked)}
                                                        />
                                                        <span>{`${category} (${categoryQuestions.length} questions)`}</span>
                                                    </div>
                                                }
                                            >
                                                {categoryQuestions.map((q) => (
                                                    <div key={q.questionId} style={{ padding: "8px 0", display: "flex", gap: "10px", alignItems: "center" }}>
                                                        <Checkbox
                                                            checked={selectedQuestionIds.includes(q.questionId)}
                                                            onChange={() => handleQuestionToggle(q.questionId)}
                                                        />
                                                        <Text>{q.questionTitle}</Text>
                                                    </div>
                                                ))}
                                            </Collapse.Panel>
                                        );
                                    })}
                                </Collapse>
                            </div>
                        </div>
                    }
                />
            )}
        </div>
    );
};

export default QuestionBank;